/**
 * Runtime URL-based noindex detection.
 *
 * Purpose: after the 2026-02 sitemap surgery (see scripts/generate-sitemap.js
 * header), we no longer emit ~1,300 thin programmatic pages in the sitemap.
 * The pages themselves still resolve at their routes so internal links, email
 * campaigns, Pinterest pins, and direct URLs keep working — but we mark them
 * <meta name="robots" content="noindex,follow"> so Google actively drops them
 * from the index and re-focuses crawl budget on our core calculators + guides.
 *
 * `noindex,follow` semantics:
 *   - "noindex" — do not surface this URL in search results
 *   - "follow"  — DO traverse links from this page and pass internal link
 *     equity to those linked pages (so the pages still contribute topical
 *     authority to their linked calculators and pillar guides)
 *
 * This helper is consumed by <SEO/> — every page that renders <SEO/> gets
 * automatic noindex application based on its route, no per-page changes.
 */

import noindexGuides from "./_generated/noindex-guides.json";

// Set for O(1) lookup during route matching.
const NOINDEX_GUIDES = new Set(noindexGuides);

// Canonical kebab-case state slugs, mirrors STATE_SLUGS in generate-sitemap.js
// so pattern boundaries stay accurate.
const STATE_SLUGS = [
  "alabama", "alaska", "arizona", "arkansas", "california",
  "colorado", "connecticut", "delaware", "florida", "georgia",
  "hawaii", "idaho", "illinois", "indiana", "iowa",
  "kansas", "kentucky", "louisiana", "maine", "maryland",
  "massachusetts", "michigan", "minnesota", "mississippi", "missouri",
  "montana", "nebraska", "nevada", "new-hampshire", "new-jersey",
  "new-mexico", "new-york", "north-carolina", "north-dakota", "ohio",
  "oklahoma", "oregon", "pennsylvania", "rhode-island", "south-carolina",
  "south-dakota", "tennessee", "texas", "utah", "vermont",
  "virginia", "washington", "west-virginia", "wisconsin", "wyoming",
];
const STATE_ALT = STATE_SLUGS.join("|");

// Themed embed bundles kept indexed; every other /embeds/{slug} is a
// per-widget thin landing that gets noindex.
const EMBED_KEEP = new Set([
  "energy-efficiency-bundle",
  "storm-recovery-bundle",
  "pre-listing-renovation-bundle",
  "outdoor-living-bundle",
]);

const PATTERNS = [
  // /state-overview hub + /state-overview/{state} leaves
  /^\/state-overview(?:\/[a-z-]+)?$/,
  // /tools/rebate-lookup/{state}
  /^\/tools\/rebate-lookup\/[a-z-]+$/,
  // /home-equity-loan-calculator/{state} — 50 state variants
  new RegExp(`^\\/home-equity-loan-calculator\\/(?:${STATE_ALT})$`),
  // /why-renovation-is-expensive is the curated all-50-states hub with
  // unique editorial + ItemList schema + inbound backlinks — it belongs
  // in sitemap-core.xml (promoted 2026-02-27) and MUST stay indexable.
  // Only the per-state leaves are thin variants and get noindex.
  // 2026-09-12 pass 92f — removed hub from this pattern set after a
  // Googlebot audit revealed a contradictory signal (sitemap said "index
  // me", meta tag said "noindex"). Almost certainly a major contributor
  // to the 7-month indexing stall on this URL.
  new RegExp(`^\\/why-is-(?:${STATE_ALT})-renovation-expensive$`),
  // Atlas hubs + variants: /*-cost-by-state and /*-cost-by-state/{variant}
  /^\/[a-z-]+-cost-by-state(?:\/[a-z-]+)?$/,
  // /outdoor-living-by-state (atlas hub sibling, no `-cost-` prefix)
  /^\/outdoor-living-by-state$/,
  // /answers/{calc-slug} per-calc sub-hubs (keep /answers itself indexed)
  /^\/answers\/[a-z-]+$/,
  // /qa/{slug} — 75 QA leaves (keep /qa hub indexed)
  /^\/qa\/[a-z0-9-]+$/,
  // Per-state calc variants like /bathroom-remodel-cost-alabama.
  // Deliberately matches only bare `<project>-cost-<state>`. Calculator
  // pages end in `-calculator` and hand-authored guides live under /guides/.
  new RegExp(`^\\/[a-z-]+-cost-(?:${STATE_ALT})$`),
  // Research-cluster state × project leaves (spotted by Bing Webmaster
  // Tools as thin state×project templates leaking indexable status):
  //   /contractor-deposit-rules/{project}-in-{state}
  //   /energy-rebates/{project}-in-{state}
  //   /renovation-timeline/{project}-in-{state}
  //   /permit-cost/{project}-in-{state}
  // Hubs (e.g. /contractor-deposit-rules with no trailing segment) remain
  // indexable and are declared in sitemap-core.xml.
  new RegExp(
    `^\\/(?:contractor-deposit-rules|energy-rebates|renovation-timeline|permit-cost)\\/[a-z0-9-]+-in-(?:${STATE_ALT})$`
  ),
];

/**
 * Return true if the given pathname should be noindex,follow.
 * @param {string} pathname — typically from `useLocation().pathname`
 */
export function shouldNoIndexRoute(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  // Normalize trailing slash (except root).
  const p = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;

  // /embeds/{slug} — noindex unless it's one of the 4 themed bundles.
  if (p.startsWith("/embeds/")) {
    const slug = p.slice("/embeds/".length);
    if (slug.includes("/")) return false; // deeper paths ignored
    return !EMBED_KEEP.has(slug);
  }

  // /guides/{slug} — noindex if slug is in the programmatic set.
  if (p.startsWith("/guides/")) {
    const slug = p.slice("/guides/".length);
    if (slug.includes("/")) return false;
    return NOINDEX_GUIDES.has(slug);
  }

  return PATTERNS.some((re) => re.test(p));
}
